import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, FlaskConical } from 'lucide-react';
import { countActiveHabits, PLAN_LIMITS } from '@habitos/protocol';
import { useApp } from '../state';
import { NeedsAccount } from './AccountInfo';
import { useI18n } from '../i18n';
import { getMeta } from '../db/db';
import { api } from '../sync/api';
import { cacheEntitlement, type SessionInfo } from '../sync/engine';
import { useSyncState } from '../sync/store';
import { useToast } from '../ui/Toast';

type Ent = SessionInfo['entitlement'];

export function PlanScreen() {
  const { t, fmtDate } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const sync = useSyncState();
  const [billing, setBilling] = useState<'none' | 'test' | 'live' | null>(null);
  const [ent, setEnt] = useState<Ent | null>(null);

  useEffect(() => {
    void getMeta<Ent | null>('entitlement', null).then(setEnt);
    api<{ billing: 'none' | 'test' | 'live' }>('/config')
      .then((c) => setBilling(c.billing))
      .catch(() => setBilling('none'));
    if (sync.signedIn) {
      api<{ entitlement: Ent }>('/billing/entitlement')
        .then(async (r) => {
          setEnt(r.entitlement);
          await cacheEntitlement(r.entitlement);
        })
        .catch(() => {});
    }
  }, [sync.signedIn]);

  const premium = ent?.plan === 'premium';
  const { data } = useApp();
  const active = countActiveHabits(data.habits);
  const limits = PLAN_LIMITS[premium ? 'premium' : 'free'];

  const checkout = async (interval: 'monthly' | 'yearly') => {
    try {
      const r = await api<{ url: string | null; test: boolean; entitlement: Ent }>('/billing/checkout', { body: { interval } });
      if (r.url) {
        window.location.assign(r.url);
        return;
      }
      setEnt(r.entitlement);
      await cacheEntitlement(r.entitlement);
      toast.show(t('plan.activated'));
    } catch {
      toast.show(t('sync.errors.generic'));
    }
  };

  return (
    <div className="stack-l">
      <header className="row">
        <button type="button" className="icon-btn" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1>{t('plan.title')}</h1>
      </header>

      {billing === 'test' && (
        <div className="banner" role="note" data-testid="billing-test-banner">
          <FlaskConical size={22} aria-hidden="true" />
          <div className="banner__body">
            <span className="small">{t('plan.testMode')}</span>
          </div>
        </div>
      )}

      <p style={{ fontWeight: 750 }}>
        {t('plan.current', { plan: premium ? t('plan.premium') : t('plan.free') })}
        {premium && ent?.currentPeriodEnd ? ` · ${t('plan.validUntil', { date: fmtDate(ent.currentPeriodEnd.slice(0, 10), { day: 'numeric', month: 'long', year: 'numeric' }) })}` : ''}
      </p>
      {premium && ent?.cancelAtPeriodEnd && <p className="muted small">{t('plan.cancelled')}</p>}

      <p className="small" data-testid="plan-usage">
        {t('plan.usage', { count: active, limit: limits.activeHabits })}
      </p>

      <section className="card stack" aria-labelledby="plan-both">
        <h2 id="plan-both">{t('plan.bothTitle')}</h2>
        <p className="small">{t('plan.both')}</p>
        <p className="muted small">{t('plan.bothAccount')}</p>
      </section>

      <section className="stack" aria-labelledby="plan-limits">
        <h2 id="plan-limits">{t('plan.limitsTitle')}</h2>
        <table className="plan-table">
          <thead>
            <tr>
              <td />
              <th scope="col">{t('plan.free')}</th>
              <th scope="col">{t('plan.premium')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t('plan.rows.habits')}</th>
              <td className="num">{PLAN_LIMITS.free.activeHabits}</td>
              <td className="num">{PLAN_LIMITS.premium.activeHabits}</td>
            </tr>
            <tr>
              <th scope="row">{t('plan.rows.reminders')}</th>
              <td className="num">{PLAN_LIMITS.free.remindersPerHabit}</td>
              <td className="num">{PLAN_LIMITS.premium.remindersPerHabit}</td>
            </tr>
            <tr>
              <th scope="row">{t('plan.rows.trends')}</th>
              <td>{PLAN_LIMITS.free.advancedStats ? t('plan.yes') : t('plan.no')}</td>
              <td>{PLAN_LIMITS.premium.advancedStats ? t('plan.yes') : t('plan.no')}</td>
            </tr>
            <tr>
              <th scope="row">{t('plan.rows.export')}</th>
              <td>{t('plan.yes')}</td>
              <td>{t('plan.yes')}</td>
            </tr>
          </tbody>
        </table>
        <p className="muted small">{t('plan.exportFree')}</p>
      </section>

      <section className="card stack" aria-labelledby="plan-at-limit">
        <h2 id="plan-at-limit">{t('plan.atLimitTitle')}</h2>
        <ul className="stack small" style={{ paddingLeft: '1.2rem', margin: 0 }}>
          {['create', 'archive', 'sync', 'expired'].map((k) => (
            <li key={k}>{t(`plan.atLimit.${k}`, { limit: PLAN_LIMITS.free.activeHabits })}</li>
          ))}
        </ul>
      </section>

      <section className="card stack" style={{ borderColor: 'var(--primary)' }} aria-labelledby="plan-premium">
        <h2 id="plan-premium">{t('plan.premium')}</h2>
        <p className="small">{t('plan.premiumAdds')}</p>
        {!sync.signedIn ? (
          <NeedsAccount feature="premium" />
        ) : billing === 'none' ? (
          <p className="muted small">{t('plan.unavailable')}</p>
        ) : premium ? (
          !ent?.cancelAtPeriodEnd && (
            <button
              type="button"
              className="btn btn--outline"
              onClick={async () => {
                try {
                  const r = await api<{ entitlement: Ent }>('/billing/cancel', { body: {} });
                  setEnt(r.entitlement);
                  await cacheEntitlement(r.entitlement);
                  toast.show(t('plan.cancelled'));
                } catch {
                  toast.show(t('sync.errors.generic'));
                }
              }}
            >
              {t('plan.cancel')}
            </button>
          )
        ) : (
          <div className="row row--wrap">
            <button type="button" className="btn btn--primary" onClick={() => void checkout('monthly')}>
              {t('plan.upgrade')} · {t('plan.monthly')}
            </button>
            <button type="button" className="btn btn--outline" onClick={() => void checkout('yearly')}>
              {t('plan.yearly')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
